import { CloseIcon } from "@chakra-ui/icons";
import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    ButtonGroup,
    Button,
    IconButton,
    Divider,
    useDisclosure,
    FormControl,
    Checkbox
} from "@chakra-ui/react";
import { Field, Formik, FormikProps } from "formik";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import hotToast from "react-hot-toast";
import { BsPlay } from "react-icons/bs";
import { BsRobot } from "react-icons/bs";
import { GrTestDesktop } from "react-icons/gr";
import { VscTools } from "react-icons/vsc";
import { useSelector } from "react-redux";
import { type Socket } from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import { CellTool } from "../../models/collaborate.model";
import { selectPersona } from "../../store/slices/persona.slice";
import { PersonaModel } from "../../types/persona";
import { useHttpClient } from "../../utils/http.utils";
import { initSocket } from "../../utils/socket.utils";
import {
    convertObjectKeyToLabel,
    getPersonaFullName,
    getPersonaProfilePicture
} from "../../utils/string";
import FormikSelect from "../form/FormikSelect";
import PersonaSelectOption from "../persona/PersonaSelectOption";
import FormikFormInputControl from "../tools/FormikFormInputControl";
import SelectedTestToolArgumentsAutoSave from "../tools/SelectedTestToolArgumentsAutoSave";
import ToolsOutput from "../tools/ToolsOutput";

type ToolsLabControlPanelProps = {
    open: boolean;
    onClose: () => void;
};

const ToolsLabControlPanel: FC<ToolsLabControlPanelProps> = (props) => {
    const toolTestDisc = useDisclosure();
    const selectedTestTool: CellTool = useSelector((state: any) => state.tools.selectedTestTool);
    const { executeToolApi } = useHttpClient();
    const [outputData, setOutputData] = useState<{
        file: string;
        text: string | { complete_output: string; system_arguments: any };
    }>();
    const [executingLoading, setExecutingLoading] = useState(false);
    const personaFormRef = useRef<FormikProps<any>>(null);
    const formikRef = useRef<FormikProps<any>>(null);
    const personas = useSelector(selectPersona);
    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        if (props.open) {
            toolTestDisc.onOpen();
        }
    }, [props.open, toolTestDisc]);

    const personaOptions = useMemo(() => {
        return personas.map((persona: PersonaModel) => ({
            label: getPersonaFullName(persona),
            value: persona.id,
            type: persona.type,
            status: persona.status,
            profile_photo: getPersonaProfilePicture(persona)
        }));
    }, [personas]);

    const argumentForm = useMemo(() => {
        const arg = selectedTestTool?.arguments_object;
        if (arg) {
            const keys = Object.keys(arg);
            /**
             * Show only arguments parameter_type equals custom or normal
             */
            return keys
                .filter(
                    (key) =>
                        arg[key].parameter_type === "user" ||
                        arg[key].parameter_type === "normal" ||
                        (arg[key].parameter_type === "custom" && arg[key].required)
                )
                .map((key) => ({
                    key,
                    description: arg[key].description,
                    required: !!arg[key].required,
                    label: convertObjectKeyToLabel(key),
                    type: arg[key].type,
                    item_type: arg[key].item_type,
                    value: arg[key].default_value || "",
                    parameter_type: arg[key].parameter_type,
                    value_schema: arg[key].value_schema || undefined,
                    key_placeholder: arg[key].key_placeholder || undefined,
                    properties: (arg[key] as any).properties || undefined,
                    button_text: arg[key].button_text || "",
                    depends_on: arg[key].depends_on || undefined,
                    adf_structure: arg[key].adf_structure || undefined,
                    options: arg[key].options || undefined,
                    items: arg[key].items || undefined,
                    is_disabled: arg[key].is_disabled || false,
                    title: arg[key].title || undefined
                }));
        } else {
            return [];
        }
    }, [selectedTestTool]);

    const closeModalHandler = () => {
        if (typeof props.onClose === "function") {
            props.onClose();
        }
        toolTestDisc.onClose();
        // Clear output data
        setOutputData(undefined);
    };

    const generateUuid = () => {
        return uuidv4();
    };

    useEffect(() => {
        if (!socketRef.current) {
            socketRef.current = initSocket();
        }

        socketRef.current.connect();

        return () => {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
            }
        };
    }, [selectedTestTool]);

    const executeToolHandler = async () => {
        const errors = await formikRef.current!.validateForm();
        const personaFormErrors = await personaFormRef.current!.validateForm();
        const executionToolId = generateUuid();

        if (Object.keys(errors).length > 0) {
            hotToast.error("Please fill all required fields");
            return;
        }

        if (Object.keys(personaFormErrors).length > 0) {
            hotToast.error("Please select a persona");
            return;
        }
        // TODO: Add a validation for the arguments
        // set loading to true
        setExecutingLoading(true);

        // Call API endpoint
        executeToolApi({
            toolId: selectedTestTool.tool_id,
            arguments: selectedTestTool.arguments_object,
            personaId: personaFormRef.current!.values.persona.value,
            testMode: true,
            executionToolId: executionToolId
        })
            .then(() => {
                socketRef.current!.on(`response-execution-${executionToolId}`, (data) => {
                    setOutputData(data.data);
                    setExecutingLoading(false);
                });

                socketRef.current!.on(`response-execution-error-${executionToolId}`, () => {
                    hotToast.error(
                        "An error occurred while executing the tool. Please try again later"
                    );
                    setExecutingLoading(false);
                });
            })
            .catch(() => {
                hotToast.error(
                    "An error occurred while executing the tool. Please try again later"
                );
                setExecutingLoading(false);
            });
    };

    return selectedTestTool ? (
        <Drawer
            isOpen={toolTestDisc.isOpen}
            placement="right"
            size="sm"
            closeOnOverlayClick={false}
            onClose={closeModalHandler}
        >
            <DrawerOverlay />
            <DrawerContent style={{ maxWidth: "unset", display: "block", width: "910px" }}>
                {/* <DrawerCloseButton /> */}
                <div className="drawer-body h-full grid grid-rows-[60px_auto] overflow-hidden">
                    <div className="toolbar px-[16px] h-full border-b flex items-center justify-between gap-[10px] border-[#555555]">
                        <p className="text-[16px] flex items-center gap-1 font-bold">
                            <GrTestDesktop size={"15px"} />
                            Tools Lab
                        </p>
                        <ButtonGroup size="sm">
                            <Button
                                title="Test Tool"
                                className="bg-green-6"
                                backgroundColor={"rgb(22 163 74)"}
                                color={"#FFF"}
                                onClick={executeToolHandler}
                                isLoading={executingLoading}
                                loadingText="Executing..."
                            >
                                <BsPlay size="22px" />
                                Execute
                            </Button>
                            {/* <IconButton aria-label="Persist" title="Persist" icon={<VscSaveAll size="18px" />} /> */}
                            <IconButton
                                onClick={closeModalHandler}
                                variant={"outline"}
                                colorScheme="red"
                                title="Close"
                                aria-label="Close"
                                icon={<CloseIcon />}
                            />
                        </ButtonGroup>
                    </div>
                    <div className="h-full overflow-hidden grid grid-cols-[408px_auto]">
                        <section className="tool-configuration h-full overflow-y-auto">
                            <section className="selected-tool min-h-[285px] pt-2 px-[20px] pb-[32px]">
                                <div className="section-header pb-[6px] mb-[4px]">
                                    <h4 className="text-[15px] flex items-center gap-[8px] font-semibold text-slate-200">
                                        <BsRobot size={"18px"} className="inline-block" />
                                        Persona
                                    </h4>
                                    <p className="text-[13px] text-neutral-400">
                                        Select a persona to be used to run the tool.
                                    </p>
                                    <Divider className="mt-2" />
                                </div>
                                <Formik
                                    innerRef={personaFormRef}
                                    initialValues={{ persona: "" }}
                                    validate={(values) => {
                                        const errors: any = {};
                                        if (!values.persona) {
                                            errors.persona = "Persona is required";
                                        }
                                        return errors;
                                    }}
                                    onSubmit={() => {}}
                                >
                                    {(formik) => (
                                        <form>
                                            <FormControl
                                                isRequired
                                                isInvalid={
                                                    !!formik.errors.persona &&
                                                    !!formik.touched.persona
                                                }
                                            >
                                                <FormikSelect
                                                    name="persona"
                                                    placeholder="Select Persona (required)"
                                                    maxMenuHeight={500}
                                                    isSearchable={true}
                                                    options={personaOptions}
                                                    components={{ Option: PersonaSelectOption }}
                                                    onBlur={() =>
                                                        formik.setFieldTouched("persona", true)
                                                    }
                                                />
                                                {!!formik.errors.persona &&
                                                    !!formik.touched.persona && (
                                                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                                                            {formik.errors.persona?.toString()}
                                                        </p>
                                                    )}
                                            </FormControl>
                                        </form>
                                    )}
                                </Formik>

                                <div className="section-header mt-[24px] pb-[6px] mb-[4px]">
                                    <h4 className="text-[15px] font-semibold text-slate-200">
                                        <VscTools size={"18px"} className="inline-block" />
                                        &nbsp;
                                        {selectedTestTool.tool_name} arguments
                                    </h4>
                                    <p className="text-[13px] text-neutral-400">
                                        Configure the arguments for{" "}
                                        {selectedTestTool.display_name ||
                                            selectedTestTool.tool_name ||
                                            ""}
                                        .
                                    </p>
                                    <Divider className="mt-2" />
                                </div>
                                {/* Persist values */}
                                <Formik
                                    innerRef={formikRef}
                                    initialValues={{
                                        $persistTestValues: false,
                                        ...argumentForm.reduce(
                                            (acc, obj) => ({ ...acc, [obj.key]: obj.value }),
                                            {}
                                        )
                                    }}
                                    onSubmit={() => {}}
                                >
                                    {(formik) => (
                                        <form className="w-full" onSubmit={formik.handleSubmit}>
                                            <Field name="$persistTestValues">
                                                {({ field }) => (
                                                    <div className="persist-control mb-[10px]">
                                                        <Checkbox {...field}>
                                                            <span className="text-blue-200 font-medium text-[14px]">
                                                                Persist Test values in cell
                                                            </span>
                                                        </Checkbox>
                                                    </div>
                                                )}
                                            </Field>
                                            {argumentForm.map((arg, index) => (
                                                <FormikFormInputControl
                                                    key={`form-control-${index}`}
                                                    name={arg.key}
                                                    argument={arg}
                                                    formikProps={formik}
                                                />
                                            ))}
                                            <SelectedTestToolArgumentsAutoSave debounceMs={500} />
                                        </form>
                                    )}
                                </Formik>
                            </section>
                        </section>

                        <section className="output-container border-l px-[10px] py-[10px] w-full h-full overflow-hidden">
                            <ToolsOutput
                                outputData={outputData}
                                isExecuting={executingLoading}
                                toolName={
                                    selectedTestTool.display_name ||
                                    selectedTestTool.tool_name ||
                                    ""
                                }
                            />
                        </section>
                    </div>
                </div>

                {/* <DrawerFooter>
                        <Button variant='outline' mr={3} onClick={toolTestDisc.onClose}>
                            Cancel
                        </Button>
                    </DrawerFooter> */}
            </DrawerContent>
        </Drawer>
    ) : (
        <></>
    );
};

export default ToolsLabControlPanel;
